import { Component, Fragment } from 'react';
import { ToastContainer, Flip } from 'react-toastify';
import { connect } from 'react-redux';
import { Progress } from 'reactstrap';
import { go, push, replace } from 'redux-first-history';
import {
    getIsAuthenticated, getFormIsOpen, getTenant, getUser, getTenantSettings, getTenantTheme,
} from '@s20/selectors/features/common.selectors';
import { getPermissionStrings } from '@s20/selectors/features/permissions.selectors';
import {
    setFormIsOpen, setIsAuthenticated, setTenant, setUser,
    setPageTitle, setTenantSettings, setTenantTheme, setTimesheetNotifications,
    setPreferredLanguage, setAllTenants,
} from '@s20/slices/features/common.slice';
import { history } from '@s20/store';
import { httpClient } from '@s20/services/httpClient';
import CommonContext, { ApiRoutes, ApplicationPermissions } from './Common';
import authService from './api-authorization/AuthorizeService';
import { ApplicationPaths } from './api-authorization/ApiAuthorizationConstants';
import { ApplyBranding } from './ApplyBranding';
import { HistoryWatcher } from './common/layout/HistoryWatcher';
import NavMenu from './NavMenu';
import { Alert, Modal } from './common/Modal';

class AppLayout extends Component {
    static displayName = AppLayout.name;

    constructor(props) {
        super(props);

        const params = new URLSearchParams(window.location.search);
        const impersonating = params.get('impersonating');
        if (impersonating) {
            window.sessionStorage.setItem('impersonating', impersonating);
            params.delete('impersonating');
            const clean = window.location.pathname
                + (params.toString() ? `?${params}` : '')
                + window.location.hash;
            window.history.replaceState(null, '', clean);
        }

        this.setFormOpened = this.setFormOpened.bind(this);
        this.setPageTitle = this.setPageTitle.bind(this);
    }

    componentDidMount() {
        this._subscription = authService.subscribe(() => this.populateState());
        this.populateState();
    }

    componentWillUnmount() {
        authService.unsubscribe(this._subscription);
    }

    checkUserHasPermission = (permission) => {
        // 2022-09-26 - M. Nicol - Refactored to reduce number of operations.
        const { permissions } = this.props;

        // Example: location.view -> ["location", "view"] -> category: location, action: view
        const [category, action] = (permission ?? '').split('.');

        if (!permissions || !category || !action) return false;

        const hasPerm = permissions.some((p) => {
            const [p_permission, p_action] = (p || '').split('.');
            return p_permission === category && p_action === action;
        });
        return hasPerm;
    };

    setFormOpened = (value) => {
        const { dispatch } = this.props;

        dispatch(setFormIsOpen(value));
    };
    setPageTitle = (value) => {
        const { dispatch } = this.props;

        dispatch(setPageTitle(value));
    };

    push = (to) => {
        const { dispatch } = this.props;

        if (typeof to === 'string') {
            dispatch(push(to));
        } else if (to.pathname) {
            if (to.state) {
                dispatch(push(to, to.state));
            } else {
                dispatch(push(to));
            }
        }
    };

    go = (delta) => {
        const { dispatch } = this.props;

        dispatch(go(delta));
    };

    replace = (to) => {
        const { dispatch } = this.props;

        if (typeof to === 'string') {
            dispatch(replace(to));
        } else if (to.pathname) {
            if (to.state) {
                dispatch(replace(to, to.state));
            } else {
                dispatch(replace(to));
            }
        }
    };

    populateState = async () => {
        const { dispatch } = this.props;
        const isAuthenticated = await authService.isAuthenticated();

        // if logged in, get the user details,
        // hydrate the context
        if (isAuthenticated) {
            const params = new URLSearchParams(window.location.search);
            const switchTenantId = params.get('switchTenantId');

            if (switchTenantId) {
                try {
                    await httpClient.put(ApiRoutes.auth.updateTenant(switchTenantId), {}, httpClient.format.json);
                    window.sessionStorage.setItem('currentTenantId', switchTenantId);
                } catch (err) {
                    console.error('Failed to switch tenant from URL param switchTenantId:', err);
                }

                params.delete('switchTenantId');
                const clean = window.location.pathname
                    + (params.toString() ? `?${params}` : '')
                    + window.location.hash;
                window.history.replaceState(null, '', clean);
            }

            const [tenant, userProfile, selfDispatchTimesheetNotifications, allTenants] = await Promise.all([
                httpClient.js(ApiRoutes.tenant.settings()),
                httpClient.js(ApiRoutes.auth.profile()),
                httpClient.js(ApiRoutes.selfDispatchTimesheet.selfDispatchNotifications()),
                httpClient.js(ApiRoutes.tenant.byUserRole()),
            ]);

            window.sessionStorage.setItem('currentTenantId', tenant.id);

            userProfile.isAdmin = (userProfile.roles ?? [])
                .map((x) => x.name)
                .includes('Administrator');

            //---Moved from getTenantUserProfile before decommissioning it - Start---
            userProfile.canCreateNonFlagTimesheet = userProfile.employeeId
                && this.checkUserHasPermission(ApplicationPermissions.nonFlagging_timesheet_create);

            // This feature is not currently implemented
            userProfile.canCreateSelfDispatchTimesheet = false;
            //---Moved from getTenantUserProfile before decommissioning it - End---

            // Apply the user's saved language preference. Anonymous users land
            // on whatever the browser-language detector picked at boot; this
            // overrides for authenticated users with a stored preference.
            const lang = userProfile.preferredLanguage;
            dispatch(setPreferredLanguage(lang));

            dispatch(setIsAuthenticated(isAuthenticated));
            dispatch(setUser(userProfile));
            dispatch(setFormIsOpen(false));
            dispatch(setTenant(tenant));
            dispatch(setTenantSettings(tenant?.tenantSettings));
            dispatch(setTenantTheme(tenant?.tenantSettings?.defaultTheme));
            dispatch(setAllTenants(allTenants));

            // TODO - This should be reactive and not just load on page load
            dispatch(setTimesheetNotifications(selfDispatchTimesheetNotifications?.count));

            // Redirect if subdomain is missing — but never during the OIDC callback flow.
            // Login.processLoginCallback navigates to the deep link at the same time, and
            // hopping the callback URL to the subdomain orphans the oidc-client-ts state
            // (localStorage is per-origin), causing signinCallback to throw and the user
            // to land on the origin root with the deep link lost.
            const onAuthRoute = window.location.pathname.startsWith(`${ApplicationPaths.ApiAuthorizationPrefix}/`);
            if (!tenant.subdomain && tenant.baseUrl !== '/' && !onAuthRoute) {
                // Guard the redirect: if tenant.baseUrl is not a valid absolute URL,
                // `new URL(tenant.baseUrl)` throws a SyntaxError that would bubble out of
                // populateState and white-screen the app (forever spinner). This happens when
                // the server misresolves the tenant (e.g. forwarded headers not trusted behind
                // a proxy chain, so tenant resolution falls back to a default with a bad
                // baseUrl). Skip the redirect and keep rendering rather than hang.
                try {
                    const target = new URL(window.location.href);
                    const base = new URL(tenant.baseUrl);
                    target.protocol = base.protocol;
                    target.host = base.host;
                    window.location.href = target.toString();
                } catch (err) {
                    console.error(`Skipping subdomain redirect: tenant.baseUrl is not a valid URL: "${tenant.baseUrl}"`, err);
                }
            }
        } else {
            dispatch(setIsAuthenticated(false));
        }
    };

    getContextValue = () => ({
        tenant: {
            ...this.props.tenant,
            tenantSettings: this.props.tenantSettings,
        },
        permissions: this.props.permissions,
        formIsOpen: this.props.formIsOpen,
        version: this.props.version,
        pageTitle: this.props.pageTitle,
        setPageTitle: this.setPageTitle,
        setFormOpened: this.setFormOpened,
        checkUserHasPermission: this.checkUserHasPermission,
        user: this.props.user,
        isAuthenticated: this.props.isAuthenticated,
        history: {
            push: this.push,
            go: this.go,
            replace: this.replace,
            location: history.location,
        },
        dispatch: this.props.dispatch,
    });

    render() {
        const {
            tenantSettings, tenantTheme, user, isAuthenticated,
        } = this.props;

        if (
            (!user && isAuthenticated)
            || isAuthenticated === null
            || (isAuthenticated && !tenantSettings)
        ) {
            return <Progress />;
        }

        return (
            <CommonContext.Provider
                value={this.getContextValue()}
            >
                <Fragment key={this.props.tenant?.id}>
                    <ApplyBranding
                        navBarHeight={60}
                        colors={tenantTheme ? {
                            primary: tenantTheme.primaryColor,
                            secondary: tenantTheme.secondaryColor,
                            success: tenantTheme.successColor,
                            warning: tenantTheme.warningColor,
                            danger: tenantTheme.dangerColor,
                            information: tenantTheme.infoColor,
                            defaultHighlight: tenantTheme.defaultHighlightColor,
                            defaultHighlightAccent: tenantTheme.defaultHighlightAccentColor,
                        } : undefined}
                    />

                    <NavMenu />

                    <main id="main">
                        <div className="container-fluid p-0">
                            {this.props.children}
                        </div>
                    </main>
                </Fragment>

                <ToastContainer
                    position="top-center"
                    transition={Flip}
                    autoClose={5000}
                    hideProgressBar={false}
                    newestOnTop
                    closeOnClick
                    rtl={false}
                    pauseOnFocusLoss
                    draggable
                    pauseOnHover
                    theme="colored"
                    icon={false}
                    stacked
                />

                <HistoryWatcher />

                <Modal />
                <Alert />
            </CommonContext.Provider>
        );
    }
}

const mapStateToProps = (state) => ({
    isAuthenticated: getIsAuthenticated(state),
    formIsOpen: getFormIsOpen(state),
    user: getUser(state),
    tenant: getTenant(state),
    permissions: getPermissionStrings(state),
    tenantSettings: getTenantSettings(state),
    tenantTheme: getTenantTheme(state),
});

export const Layout = connect(mapStateToProps)(AppLayout);